import React, { useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";

const EVENT_LABELS = {
    demo_started: "Demo",
    emergency_pressed: "Notfall-Knopf",
    called_112: "Notruf",
    cpr_started: "Reanimation",
};

function formatDuration(ms) {
    if (!ms) return "—";
    const s = Math.round(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}m ${r}s`;
}

function buildDayOptions() {
    const opts = [{ value: "", label: "Letzte 24h (rollend)" }];
    const now = new Date();
    for (let i = 0; i < 7; i++) {
        const d = new Date(
            Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - i),
        );
        const iso = d.toISOString().slice(0, 10);
        let label;
        if (i === 0) label = "Heute (00:00 UTC)";
        else if (i === 1) label = "Gestern";
        else if (i === 2) label = "Vorgestern";
        else label = `vor ${i} Tagen`;
        opts.push({ value: iso, label: `${label} · ${iso}` });
    }
    return opts;
}

export default function LiveSessions({ mode }) {
    const [date, setDate] = useState("");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    const load = async (d, m) => {
        setLoading(true);
        setErr("");
        try {
            const res = await adminApi.sessions(d, m, 500);
            setData(res);
        } catch (ex) {
            setErr("Sessions konnten nicht geladen werden.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // eslint-disable-next-line
        load(date, mode);
    }, [date, mode]);

    const days = buildDayOptions();

    return (
        <section
            className="bg-white border border-zinc-200 rounded-2xl shadow-sm overflow-hidden"
            data-testid="admin-live-sessions"
        >
            <div className="px-4 sm:px-6 py-3 border-b border-zinc-200 flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <h2 className="text-sm font-semibold text-zinc-600">
                        Live Sessions (24h-Fenster)
                    </h2>
                    {data && (
                        <div className="text-xs text-zinc-500">
                            {data.total} Sessions ·{" "}
                            {new Date(data.windowStart).toLocaleString("de-DE")}{" "}
                            – {new Date(data.windowEnd).toLocaleString("de-DE")}
                        </div>
                    )}
                </div>
                <select
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    data-testid="admin-sessions-day"
                    className="px-3 py-2 border border-zinc-300 rounded-lg text-sm bg-white"
                >
                    {days.map((o) => (
                        <option key={o.value || "rolling"} value={o.value}>
                            {o.label}
                        </option>
                    ))}
                </select>
            </div>

            {err && (
                <div className="px-4 py-3 text-sm text-red-700 bg-red-50">
                    {err}
                </div>
            )}
            {loading && (
                <div className="px-4 py-3 text-sm text-zinc-500">Lade…</div>
            )}

            {data && !loading && (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-zinc-50 text-zinc-600">
                            <tr>
                                <th className="text-left px-4 py-2 font-medium">
                                    Zeit
                                </th>
                                <th className="text-left px-4 py-2 font-medium">
                                    Modus
                                </th>
                                <th className="text-left px-4 py-2 font-medium">
                                    Land
                                </th>
                                <th className="text-left px-4 py-2 font-medium">
                                    Sprache
                                </th>
                                <th className="text-left px-4 py-2 font-medium">
                                    Dauer
                                </th>
                                <th className="text-left px-4 py-2 font-medium">
                                    Letzter Schritt
                                </th>
                                <th className="text-left px-4 py-2 font-medium">
                                    Wichtige Aktionen
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.items.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={7}
                                        className="px-4 py-6 text-center text-zinc-500"
                                    >
                                        Keine Sessions in diesem Zeitraum.
                                    </td>
                                </tr>
                            )}
                            {data.items.map((s, i) => (
                                <tr
                                    key={s.sessionId || i}
                                    className="border-t border-zinc-100"
                                    data-testid={`admin-session-row-${i}`}
                                >
                                    <td className="px-4 py-2 text-zinc-500 whitespace-nowrap">
                                        {s.ts
                                            ? new Date(s.ts).toLocaleString(
                                                  "de-DE",
                                              )
                                            : "—"}
                                    </td>
                                    <td className="px-4 py-2">
                                        {s.demoMode ? (
                                            <span className="inline-block px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-xs font-medium">
                                                Demo
                                            </span>
                                        ) : (
                                            <span className="inline-block px-2 py-0.5 rounded bg-red-100 text-red-800 text-xs font-medium">
                                                Echt
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-2 text-zinc-700 font-mono">
                                        {s.country || "—"}
                                    </td>
                                    <td className="px-4 py-2 text-zinc-700">
                                        {s.locale || "—"}
                                    </td>
                                    <td className="px-4 py-2 text-zinc-900 font-medium tabular-nums">
                                        {formatDuration(s.durationMs)}
                                    </td>
                                    <td className="px-4 py-2 text-zinc-700">
                                        {s.maxStep || "—"}
                                    </td>
                                    <td className="px-4 py-2">
                                        <div className="flex flex-wrap gap-1">
                                            {(s.events || []).map((e) => (
                                                <span
                                                    key={e}
                                                    className="inline-block px-2 py-0.5 rounded bg-zinc-100 text-zinc-700 text-xs"
                                                >
                                                    {EVENT_LABELS[e] || e}
                                                </span>
                                            ))}
                                            {s.called112 &&
                                                !(s.events || []).includes(
                                                    "called_112",
                                                ) && (
                                                    <span className="inline-block px-2 py-0.5 rounded bg-red-100 text-red-800 text-xs">
                                                        Notruf
                                                    </span>
                                                )}
                                            {s.reachedCpr &&
                                                !(s.events || []).includes(
                                                    "cpr_started",
                                                ) && (
                                                    <span className="inline-block px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 text-xs">
                                                        Reanimation
                                                    </span>
                                                )}
                                            {(s.events || []).length === 0 &&
                                                !s.called112 &&
                                                !s.reachedCpr && (
                                                    <span className="text-zinc-400 text-xs">
                                                        —
                                                    </span>
                                                )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
}
